import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { routes } from '@/lib/routes';
import { ordersPubApi } from './api';
import { OrdersPublicationsHub } from './components/OrdersPublicationsHub';
import { AuthorIntakeView } from './components/AuthorIntakeView';
import { AuthorIntakeModal } from './components/AuthorIntakeModal';
import { ClientOrdersCrmView } from './components/ClientOrdersCrmView';
import { ClientOrderDetailModal } from './components/ClientOrderDetailModal';
import { SharedArticlesView } from './components/SharedArticlesView';
import { SharedArticleDetailModal } from './components/SharedArticleDetailModal';
import { CreateOrderModal } from './components/CreateOrderModal';
import { RoleAcademicConnectionsOverlay } from '@/features/communications';
import './orders-publications.css';
import './business-operations.css';
import type {
  ActiveTab,
  AuthorIntakeItem,
  ClientOrderItem,
  IntakeFilterKind,
  OrdersHubOverview,
  SharedArticleItem,
} from './types';

interface Props {
  onClose?: () => void;
  defaultTab?: ActiveTab;
}

function tabFromPath(pathname: string): ActiveTab | null {
  if (pathname.includes('/orders/intakes')) return 'INTAKES';
  if (pathname.includes('/orders/crm')) return 'ORDERS';
  if (pathname.includes('/orders/articles')) return 'ARTICLES';
  return null;
}

export const OrdersPublicationsPage: React.FC<Props> = ({ onClose, defaultTab = 'HUB' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathTab = tabFromPath(location.pathname);

  const [activeTab, setActiveTab] = useState<ActiveTab>(pathTab ?? defaultTab);
  const [overview, setOverview] = useState<OrdersHubOverview>({
    intake_stats: {
      total: 6,
      services: 2,
      ready_articles: 2,
      author_data: 1,
      coauthor_search: 1,
      on_moderation: 1,
    },
    orders_stats: {
      total_matrix: 0,
      published_count: 0,
      accepted_count: 0,
      in_progress_count: 0,
      coauthor_count: 0,
      filtered_count: 0,
      total_records: 0,
      pending_remaining: 0,
      total_amount_sum: 0,
      total_paid_sum: 0,
    },
    shared_stats: {
      total_cards: 5,
      stages_count: 5,
      in_progress_count: 3,
      coeditors_count: 2,
    },
  });

  const [intakes, setIntakes] = useState<AuthorIntakeItem[]>([]);
  const [intakeFilter, setIntakeFilter] = useState<IntakeFilterKind>('ALL');

  const [orders, setOrders] = useState<ClientOrderItem[]>([]);
  const [ordersStatusFilter, setOrdersStatusFilter] = useState('Все');
  const [ordersDbFilter, setOrdersDbFilter] = useState('Все');
  const [ordersPaymentFilter, setOrdersPaymentFilter] = useState('Все');
  const [ordersTypeFilter, setOrdersTypeFilter] = useState('Все');
  const [ordersSearch, setOrdersSearch] = useState('');

  const [articles, setArticles] = useState<SharedArticleItem[]>([]);

  const [selectedIntake, setSelectedIntake] = useState<AuthorIntakeItem | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<ClientOrderItem | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<SharedArticleItem | null>(null);

  const [isCreateOrderOpen, setIsCreateOrderOpen] = useState(false);
  const [discussionTarget, setDiscussionTarget] = useState<{
    type: 'INTAKE' | 'ORDER' | 'ARTICLE';
    id: string;
    title: string;
  } | null>(null);

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(routes.owner);
    }
  };

  const loadOverview = async () => {
    try {
      const data = await ordersPubApi.getOverview();
      setOverview(data);
    } catch {}
  };

  const loadIntakes = async (kind = intakeFilter) => {
    try {
      const res = await ordersPubApi.listIntakes({ kind });
      setIntakes(res.items);
      setOverview((prev) => ({ ...prev, intake_stats: res.stats }));
    } catch {}
  };

  const loadOrders = async () => {
    try {
      const res = await ordersPubApi.listOrders({
        status: ordersStatusFilter,
        database: ordersDbFilter,
        payment: ordersPaymentFilter,
        type: ordersTypeFilter,
        search: ordersSearch,
      });
      setOrders(res.items);
      setOverview((prev) => ({ ...prev, orders_stats: res.stats }));
    } catch {}
  };

  const loadArticles = async () => {
    try {
      const res = await ordersPubApi.listArticles();
      setArticles(res.items);
      setOverview((prev) => ({ ...prev, shared_stats: res.stats }));
    } catch {}
  };

  useEffect(() => {
    const next = tabFromPath(location.pathname);
    if (next) setActiveTab(next);
  }, [location.pathname]);

  useEffect(() => {
    loadOverview();
    loadIntakes();
    loadOrders();
    loadArticles();
  }, []);

  useEffect(() => {
    if (activeTab === 'INTAKES') {
      loadIntakes(intakeFilter);
    } else if (activeTab === 'ORDERS') {
      loadOrders();
    } else if (activeTab === 'ARTICLES') {
      loadArticles();
    }
  }, [activeTab, intakeFilter, ordersStatusFilter, ordersDbFilter, ordersPaymentFilter, ordersTypeFilter, ordersSearch]);

  const handleUpdateIntakeStatus = async (id: string, status: string, comment?: string) => {
    const updated = await ordersPubApi.updateIntakeStatus(id, { status, comment });
    setSelectedIntake(updated);
    loadIntakes(intakeFilter);
  };

  const handleModerateIntake = async (id: string, decision: string, comment?: string) => {
    const updated = await ordersPubApi.moderateIntake(id, { decision, comment });
    setSelectedIntake(updated);
    loadIntakes(intakeFilter);
  };

  const handleLinkIntake = async (id: string, orderId?: string, articleId?: string) => {
    const updated = await ordersPubApi.linkIntake(id, { orderId, articleId });
    setSelectedIntake(updated);
    loadIntakes(intakeFilter);
  };

  const handleUpdateOrder = async (id: string, payload: Partial<ClientOrderItem>) => {
    const updated = await ordersPubApi.updateOrder(id, payload);
    setSelectedOrder(updated);
    loadOrders();
  };

  const handleAddPayment = async (orderId: string, payload: any) => {
    await ordersPubApi.addPayment(orderId, payload);
    const updated = await ordersPubApi.getOrderDetail(orderId);
    setSelectedOrder(updated);
    loadOrders();
  };

  const handleCreateOrder = async (payload: Partial<ClientOrderItem>) => {
    await ordersPubApi.createOrder(payload);
    loadOrders();
  };

  const handleUpdateArticle = async (id: string, payload: Partial<SharedArticleItem>) => {
    const updated = await ordersPubApi.updateArticle(id, payload);
    setSelectedArticle(updated);
    loadArticles();
  };

  const handleOpenOrderById = async (orderId: string) => {
    try {
      const ord = await ordersPubApi.getOrderDetail(orderId);
      setSelectedOrder(ord);
      setActiveTab('ORDERS');
    } catch {
      setActiveTab('ORDERS');
    }
  };

  const handleViewOrder = async (order: ClientOrderItem) => {
    setSelectedOrder(order);
    try {
      const full = await ordersPubApi.getOrderDetail(order.id);
      setSelectedOrder(full);
    } catch {
      /* list row remains until detail loads */
    }
  };

  const handleViewArticle = async (article: SharedArticleItem) => {
    setSelectedArticle(article);
    try {
      const full = await ordersPubApi.getArticleDetail(article.id);
      setSelectedArticle(full);
    } catch {
      /* list row remains until detail loads */
    }
  };

  const openDiscussion = (target: {
    type: 'INTAKE' | 'ORDER' | 'ARTICLE';
    id: string;
    title: string;
  }) => {
    setDiscussionTarget(target);
  };

  const handleOpenLinkedObject = (linkedObject: { type: string; id: string; title?: string }) => {
    setDiscussionTarget(null);
    if (linkedObject.type === 'ORDER') {
      void handleOpenOrderById(linkedObject.id);
      return;
    }
    if (linkedObject.type === 'ARTICLE') {
      void ordersPubApi.getArticleDetail(linkedObject.id).then((article) => {
        setSelectedArticle(article);
        setActiveTab('ARTICLES');
      }).catch(() => {
        setActiveTab('ARTICLES');
      });
    }
  };

  const orderDetailSlot = selectedOrder ? (
    <ClientOrderDetailModal
      order={selectedOrder}
      embedded
      onClose={() => setSelectedOrder(null)}
      onUpdateOrder={handleUpdateOrder}
      onAddPayment={handleAddPayment}
      onOpenDiscussion={(order) =>
        openDiscussion({
          type: 'ORDER',
          id: order.id,
          title: order.article_title,
        })
      }
    />
  ) : null;

  const articleDetailSlot = selectedArticle ? (
    <SharedArticleDetailModal
      article={selectedArticle}
      embedded
      onClose={() => setSelectedArticle(null)}
      onUpdateArticle={handleUpdateArticle}
      onOpenOrder={handleOpenOrderById}
      onOpenDiscussion={(art) =>
        openDiscussion({
          type: 'ARTICLE',
          id: art.id,
          title: art.title,
        })
      }
    />
  ) : null;

  return (
    <div className="orders-pub-root owner-hub-page--flush">
      {activeTab === 'HUB' ? (
        <OrdersPublicationsHub
          onSelectTab={(tab) => setActiveTab(tab)}
          onClose={handleClose}
        />
      ) : activeTab === 'ORDERS' ? (
        <ClientOrdersCrmView
          orders={orders}
          stats={overview.orders_stats}
          statusFilter={ordersStatusFilter}
          databaseFilter={ordersDbFilter}
          paymentFilter={ordersPaymentFilter}
          typeFilter={ordersTypeFilter}
          searchQuery={ordersSearch}
          onStatusFilterChange={setOrdersStatusFilter}
          onDatabaseFilterChange={setOrdersDbFilter}
          onPaymentFilterChange={setOrdersPaymentFilter}
          onTypeFilterChange={setOrdersTypeFilter}
          onSearchChange={setOrdersSearch}
          onViewOrder={(order) => void handleViewOrder(order)}
          onCreateOrderClick={() => setIsCreateOrderOpen(true)}
          onBack={() => {
            setSelectedOrder(null);
            setActiveTab('HUB');
          }}
          onClose={handleClose}
          onClearDetail={() => setSelectedOrder(null)}
          detailOrder={selectedOrder}
          detailSlot={orderDetailSlot}
        />
      ) : activeTab === 'INTAKES' ? (
        <AuthorIntakeView
          items={intakes}
          stats={overview.intake_stats}
          currentFilter={intakeFilter}
          onFilterChange={(kind) => setIntakeFilter(kind)}
          onViewIntake={(item) => setSelectedIntake(item)}
          onOpenDiscussion={(item) =>
            openDiscussion({
              type: 'INTAKE',
              id: item.id,
              title: item.subject_title,
            })
          }
          onBack={() => setActiveTab('HUB')}
          onClose={handleClose}
        />
      ) : (
        <SharedArticlesView
          articles={articles}
          stats={overview.shared_stats}
          onViewArticle={(article) => void handleViewArticle(article)}
          onCreateArticleClick={() => setIsCreateOrderOpen(true)}
          onOpenSourceBase={() => {
            setSelectedArticle(null);
            setActiveTab('ORDERS');
          }}
          onBack={() => {
            setSelectedArticle(null);
            setActiveTab('HUB');
          }}
          onClose={handleClose}
          onClearDetail={() => setSelectedArticle(null)}
          detailArticle={selectedArticle}
          detailSlot={articleDetailSlot}
        />
      )}

      {selectedIntake && (
        <AuthorIntakeModal
          intake={selectedIntake}
          onClose={() => setSelectedIntake(null)}
          onUpdateStatus={handleUpdateIntakeStatus}
          onModerate={handleModerateIntake}
          onLinkOrder={handleLinkIntake}
          onOpenDiscussion={(item) =>
            openDiscussion({
              type: 'INTAKE',
              id: item.id,
              title: item.subject_title,
            })
          }
        />
      )}

      {selectedOrder && activeTab !== 'ORDERS' ? (
        <ClientOrderDetailModal
          order={selectedOrder}
          embedded={false}
          onClose={() => setSelectedOrder(null)}
          onUpdateOrder={handleUpdateOrder}
          onAddPayment={handleAddPayment}
          onOpenDiscussion={(order) =>
            openDiscussion({
              type: 'ORDER',
              id: order.id,
              title: order.article_title,
            })
          }
        />
      ) : null}

      {isCreateOrderOpen && (
        <CreateOrderModal
          onClose={() => setIsCreateOrderOpen(false)}
          onSubmit={handleCreateOrder}
        />
      )}

      {discussionTarget ? (
        <RoleAcademicConnectionsOverlay
          isOpen
          onClose={() => setDiscussionTarget(null)}
          roleContext="SYSTEM_OWNER"
          roleTitle="Владелец системы"
          userRoleCodes={['SYSTEM_OWNER']}
          onOpenLinkedObject={handleOpenLinkedObject}
          initialTarget={{
            linkedObject: {
              type: discussionTarget.type,
              id: discussionTarget.id,
              title: discussionTarget.title,
            },
          }}
        />
      ) : null}
    </div>
  );
};

export default OrdersPublicationsPage;
